import React, { useState, useEffect, useMemo, Activity } from 'react';

// third party
import { IntlProvider, MessageFormatElement } from 'react-intl';
import useConfig from 'hooks/useConfig';

// types
import { I18n } from 'types/config';

// load locales files
function loadLocaleData(i18n: I18n) {
  switch (i18n) {
    case 'de':
      return import('utils/locales/de.json');
    case 'fr':
      return import('utils/locales/fr.json');
    // case 'zh':
    //   return import('utils/locales/zh.json');
    default:
      return import('utils/locales/en.json');
  }
}

// ==============================|| LOCALIZATION ||============================== //

interface LocalsProps {
  children: React.ReactNode;
}

function decodeHtmlEntities(value: string): string {
  if (typeof document === 'undefined' || !value.includes('&')) {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function normalizeMessages(
  rawMessages: Record<string, string> | Record<string, MessageFormatElement[]> | undefined
): Record<string, string> | Record<string, MessageFormatElement[]> | undefined {
  if (!rawMessages) {
    return rawMessages;
  }

  const normalized: Record<string, string | MessageFormatElement[]> = {};

  Object.entries(rawMessages).forEach(([key, value]) => {
    normalized[key] = typeof value === 'string' ? decodeHtmlEntities(value) : value;
  });

  return normalized as Record<string, string> | Record<string, MessageFormatElement[]>;
}

export default function Locales({ children }: LocalsProps) {
  const {
    state: { i18n }
  } = useConfig();
  const [messages, setMessages] = useState<Record<string, string> | Record<string, MessageFormatElement[]> | undefined>();

  const localeDataPromise = useMemo(() => loadLocaleData(i18n), [i18n]);
  useEffect(() => {
    localeDataPromise.then((d: { default: Record<string, string> | Record<string, MessageFormatElement[]> | undefined }) => {
      setMessages(normalizeMessages(d.default));
    });
  }, [localeDataPromise]);

  return (
    <>
      <Activity mode={messages ? 'visible' : 'hidden'}>
        <IntlProvider locale={i18n} defaultLocale="de" messages={messages}>
          {children}
        </IntlProvider>
      </Activity>
    </>
  );
}
